import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';

const DECRYPT_CACHE_LIMIT = 200;

export class Crypto {
  /**
   * Ciphertext → decrypted plaintext ('' when AES yields nothing). Only the AES step is cached;
   * the plaintext is still JSON.parsed on every call so each caller gets its own copy to mutate.
   */
  private static readonly plaintextCache = new Map<string, string>();

  // Encrypt any JSON-serializable data
  public static encrypt(data: any): string {
    const dataString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(dataString, environment.secretKey).toString();
  }

  // Decrypt an encrypted string back to original JSON
  public static decrypt(encryptedData: string): any {
    if (!encryptedData) return null;

    // Try AES decryption first
    try {
      const decryptedString = Crypto.decryptToPlaintext(encryptedData);
      if (decryptedString) {
        try {
          return JSON.parse(decryptedString);
        } catch (e) {
          // If decrypted string is not JSON, return raw string
          return decryptedString;
        }
      }
    } catch (e) {
      // continue to fallback
    }

    // Fallback: maybe the stored value is plain JSON or a raw string
    try {
      return JSON.parse(encryptedData);
    } catch (e) {
      return encryptedData;
    }
  }

  private static decryptToPlaintext(encryptedData: string): string {
    const isCacheable = typeof encryptedData === 'string';
    if (isCacheable && Crypto.plaintextCache.has(encryptedData)) {
      return Crypto.plaintextCache.get(encryptedData);
    }

    // A throw here is not cached, so the caller's fallback runs exactly as before.
    const bytes = CryptoJS.AES.decrypt(encryptedData, environment.secretKey);
    const decryptedString = (bytes && bytes.toString(CryptoJS.enc.Utf8)) || '';

    if (isCacheable) {
      if (Crypto.plaintextCache.size >= DECRYPT_CACHE_LIMIT) {
        Crypto.plaintextCache.delete(Crypto.plaintextCache.keys().next().value);
      }
      Crypto.plaintextCache.set(encryptedData, decryptedString);
    }
    return decryptedString;
  }
}
